(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function chromeOffset() {
    return (
      Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-group-offset')
      ) || 72
    );
  }

  function initCareNavPin(root, nav) {
    if (!nav) return () => {};

    let placeholder = root.querySelector('[data-care-nav-placeholder]');
    if (!placeholder) {
      placeholder = document.createElement('div');
      placeholder.className = 'care-story__nav-placeholder';
      placeholder.setAttribute('data-care-nav-placeholder', '');
      placeholder.setAttribute('aria-hidden', 'true');
      nav.insertAdjacentElement('afterend', placeholder);
    }

    let sentinel = root.querySelector('[data-care-nav-sentinel]');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.setAttribute('data-care-nav-sentinel', '');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'height:0;margin:0;padding:0;pointer-events:none;';
      nav.parentNode.insertBefore(sentinel, nav);
    }

    const gallery =
      document.querySelector('[data-pdp-gallery]') ||
      document.querySelector('.pdp__gallery') ||
      document.querySelector('.pdp__stage');

    const update = () => {
      const mobile = window.matchMedia('(max-width: 899px)').matches;
      if (!mobile) {
        nav.classList.remove('is-pinned', 'is-pin-enter');
        placeholder.classList.remove('is-active');
        placeholder.style.height = '';
        nav.style.top = '';
        return;
      }

      const offset = chromeOffset();
      const navH = nav.offsetHeight || 52;
      const sentinelTop = sentinel.getBoundingClientRect().top;
      const rootBottom = root.getBoundingClientRect().bottom;
      const galleryBottom = gallery ? gallery.getBoundingClientRect().bottom : sentinelTop;

      // Pin just after the product image scrolls under the header
      const pastProductImage = galleryBottom <= offset + 2;
      const stillInStory = rootBottom > offset + navH + 12;
      const shouldPin = pastProductImage && stillInStory;
      const needPlaceholder = shouldPin && sentinelTop <= offset + 2;

      if (shouldPin) {
        const wasPinned = nav.classList.contains('is-pinned');
        nav.style.top = `${offset}px`;
        nav.classList.add('is-pinned');
        if (!wasPinned) {
          nav.classList.remove('is-pin-enter');
          void nav.offsetWidth;
          nav.classList.add('is-pin-enter');
        }
        if (needPlaceholder) {
          placeholder.style.height = `${navH}px`;
          placeholder.classList.add('is-active');
        } else {
          placeholder.classList.remove('is-active');
          placeholder.style.height = '';
        }
      } else {
        nav.classList.remove('is-pinned', 'is-pin-enter');
        placeholder.classList.remove('is-active');
        placeholder.style.height = '';
        nav.style.top = '';
      }
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    document.documentElement.addEventListener('mukhlis:chrome-offset', update);
    update();
    window.setTimeout(update, 350);
    return update;
  }

  function initCareStory(root) {
    const nav = root.querySelector('[data-care-nav]');
    const tabs = [...root.querySelectorAll('[data-care-tab]')];
    const sections = [...root.querySelectorAll('[data-care-section]')];
    const refreshPin = initCareNavPin(root, nav);

    const scrollToSection = (key) => {
      const target = root.querySelector(`[data-care-section="${key}"]`);
      if (!target) return;
      const navH = nav?.offsetHeight || 0;
      const headerOffset = chromeOffset();
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - navH - 8;
      window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
      window.setTimeout(refreshPin, reduce ? 0 : 420);
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabs.forEach((t) => t.classList.remove('is-active'));
        tab.classList.add('is-active');
        scrollToSection(tab.dataset.careTab);
      });
    });

    if ('IntersectionObserver' in window) {
      const sectionIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const key = entry.target.getAttribute('data-care-section');
            tabs.forEach((tab) => {
              tab.classList.toggle('is-active', tab.dataset.careTab === key);
            });
          });
        },
        { rootMargin: '-35% 0px -50% 0px', threshold: 0.01 }
      );
      sections.forEach((section) => sectionIo.observe(section));

      const revealIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-inview');
            revealIo.unobserve(entry.target);
          });
        },
        { threshold: 0.18, rootMargin: '0px 0px -8% 0px' }
      );
      root.querySelectorAll('[data-care-reveal]').forEach((el) => revealIo.observe(el));
    } else {
      root.querySelectorAll('[data-care-reveal]').forEach((el) => el.classList.add('is-inview'));
    }

    // Ingredient filters: All / Skin / Hair
    const ingFilters = [...root.querySelectorAll('[data-ing-filter]')];
    const ingCards = [...root.querySelectorAll('.care-ing-card[data-ing-cat]')];
    const filterIngredients = (key) => {
      ingFilters.forEach((btn) => {
        const active = btn.dataset.ingFilter === key;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', String(active));
      });
      let visibleIndex = 0;
      ingCards.forEach((card) => {
        const match = key === 'all' || card.dataset.ingCat === key;
        card.classList.remove('is-filtering-in');
        card.classList.toggle('is-hidden', !match);
        card.classList.toggle('is-visible', match);
        if (match) {
          card.style.setProperty('--fi', String(visibleIndex));
          visibleIndex += 1;
          // replay entrance
          void card.offsetWidth;
          card.classList.add('is-inview', 'is-filtering-in');
        }
      });
    };
    ingFilters.forEach((btn) => {
      btn.addEventListener('click', () => filterIngredients(btn.dataset.ingFilter));
    });

    // Mobile Details / How to use tabs
    const detailsTabs = [...root.querySelectorAll('[data-details-tab]')];
    const detailsPanels = [...root.querySelectorAll('[data-details-panel]')];
    const setDetailsTab = (key) => {
      detailsTabs.forEach((t) => {
        const active = t.dataset.detailsTab === key;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', String(active));
      });
      detailsPanels.forEach((panel) => {
        const active = panel.dataset.detailsPanel === key;
        panel.classList.toggle('is-active', active);
        if (active) panel.classList.add('is-inview');
      });
    };
    detailsTabs.forEach((tab) => {
      tab.addEventListener('click', () => setDetailsTab(tab.dataset.detailsTab));
    });
    if (detailsPanels.length) setDetailsTab('details');

    // Customer video reviews — auto + manual horizontal scroll
    const videosRoot = root.querySelector('[data-care-videos]');
    const videosRail = root.querySelector('[data-care-videos-rail]');
    if (videosRoot && videosRail) {
      let raf = 0;
      let running = false;
      let onScreen = true;
      let resumeTimer = null;
      let edgePauseUntil = 0;
      let direction = 1;
      let lastWritten = 0;
      let fingerDown = false;
      let lastUserDir = 0;
      let touchStartX = 0;
      let touchStartLeft = 0;
      const speed = reduce ? 0 : 0.5;
      const edgePauseMs = 1400;
      const resumeMs = 2800;

      const stopLoop = () => {
        running = false;
        if (raf) {
          window.cancelAnimationFrame(raf);
          raf = 0;
        }
      };

      // Measured on resize only: reading scrollWidth inside the rAF loop forced a layout every frame.
      let maxCached = Math.max(0, videosRail.scrollWidth - videosRail.clientWidth);
      if ('ResizeObserver' in window) {
        new ResizeObserver(() => {
          maxCached = Math.max(0, videosRail.scrollWidth - videosRail.clientWidth);
        }).observe(videosRail);
      }
      const maxScroll = () => maxCached;

      const tick = () => {
        if (!running || !onScreen || fingerDown || speed <= 0) {
          raf = 0;
          return;
        }

        const max = maxScroll();
        if (max <= 4) {
          raf = window.requestAnimationFrame(tick);
          return;
        }

        const current = videosRail.scrollLeft;
        if (Math.abs(current - lastWritten) > Math.max(2.5, speed * 4)) {
          lastUserDir = current < lastWritten ? -1 : 1;
          direction = lastUserDir;
          lastWritten = current;
          stopLoop();
          scheduleResume();
          return;
        }

        if (performance.now() >= edgePauseUntil) {
          let next = current + speed * direction;
          if (next >= max) {
            next = max;
            direction = -1;
            lastUserDir = -1;
            edgePauseUntil = performance.now() + edgePauseMs;
          } else if (next <= 0) {
            next = 0;
            direction = 1;
            lastUserDir = 1;
            edgePauseUntil = performance.now() + edgePauseMs;
          }
          videosRail.scrollLeft = next;
          lastWritten = videosRail.scrollLeft;
        }

        raf = window.requestAnimationFrame(tick);
      };

      const startLoop = () => {
        if (fingerDown || !onScreen || speed <= 0) return;
        stopLoop();
        running = true;
        lastWritten = videosRail.scrollLeft;
        raf = window.requestAnimationFrame(tick);
      };

      const scheduleResume = (delay = resumeMs) => {
        window.clearTimeout(resumeTimer);
        resumeTimer = window.setTimeout(() => {
          if (fingerDown) return;
          const max = maxScroll();
          const left = videosRail.scrollLeft;

          if (lastUserDir < 0 && left > 6) {
            direction = -1;
            startLoop();
            return;
          }

          if (left <= 6) {
            direction = 1;
            lastUserDir = 1;
            videosRail.scrollLeft = 0;
            lastWritten = 0;
            edgePauseUntil = performance.now() + edgePauseMs;
          } else if (left >= max - 6) {
            direction = -1;
            lastUserDir = -1;
            edgePauseUntil = performance.now() + edgePauseMs;
          } else if (lastUserDir !== 0) {
            direction = lastUserDir;
          }

          startLoop();
        }, delay);
      };

      const holdForUser = () => {
        window.clearTimeout(resumeTimer);
        stopLoop();
        lastWritten = videosRail.scrollLeft;
      };

      videosRail.addEventListener(
        'touchstart',
        (e) => {
          fingerDown = true;
          touchStartX = e.changedTouches[0]?.clientX ?? 0;
          touchStartLeft = videosRail.scrollLeft;
          holdForUser();
        },
        { passive: true, capture: true }
      );

      videosRail.addEventListener(
        'touchmove',
        () => {
          holdForUser();
        },
        { passive: true, capture: true }
      );

      const releaseFinger = (e) => {
        const x = e.changedTouches?.[0]?.clientX;
        if (typeof x === 'number') {
          const dx = x - touchStartX;
          const dScroll = videosRail.scrollLeft - touchStartLeft;
          if (Math.abs(dScroll) > 2) lastUserDir = dScroll > 0 ? 1 : -1;
          else if (Math.abs(dx) > 10) lastUserDir = dx < 0 ? 1 : -1;
        }
        fingerDown = false;
        lastWritten = videosRail.scrollLeft;
        scheduleResume();
      };
      videosRail.addEventListener('touchend', releaseFinger, { passive: true, capture: true });
      videosRail.addEventListener('touchcancel', releaseFinger, { passive: true, capture: true });

      videosRail.addEventListener('mouseenter', () => {
        if (window.matchMedia('(hover: hover)').matches) holdForUser();
      });
      videosRail.addEventListener('mouseleave', () => {
        if (window.matchMedia('(hover: hover)').matches) {
          fingerDown = false;
          scheduleResume(1200);
        }
      });

      videosRail.addEventListener(
        'wheel',
        (e) => {
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 2) {
            holdForUser();
            lastUserDir = e.deltaX > 0 ? 1 : -1;
            direction = lastUserDir;
            scheduleResume(2000);
          }
        },
        { passive: true }
      );

      videosRoot.querySelectorAll('[data-care-video-card]').forEach((card) => {
        const btn = card.querySelector('[data-care-video-play]');
        const video = card.querySelector('[data-care-video]');
        if (!btn || !video) return;

        btn.addEventListener('click', async () => {
          holdForUser();
          const isActive = card.classList.contains('is-playing');
          root.querySelectorAll('[data-care-video-card]').forEach((other) => {
            if (other === card) return;
            other.classList.remove('is-playing');
            const ov = other.querySelector('[data-care-video]');
            if (ov) {
              ov.pause();
              ov.muted = true;
            }
          });

          if (isActive) {
            video.pause();
            video.muted = true;
            card.classList.remove('is-playing');
            scheduleResume(1800);
            return;
          }

          try {
            video.muted = false;
            await video.play();
            card.classList.add('is-playing');
          } catch (_) {
            video.muted = true;
            try {
              await video.play();
              card.classList.add('is-playing');
            } catch (__) {
              scheduleResume(1200);
            }
          }
        });
      });

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              onScreen = entry.isIntersecting;
              if (!onScreen) holdForUser();
              else if (!fingerDown) scheduleResume(800);
            });
          },
          { threshold: 0.12 }
        );
        io.observe(videosRoot);
      }

      // Lite mode (motion.js, low-end phones): no auto-crawl, the rail is swipe / tap only.
      if (speed > 0 && !document.documentElement.classList.contains('mk-lite')) startLoop();
    }

    // Talbeena ingredient videos — autoplay muted when visible
    const ttIngCards = [...root.querySelectorAll('[data-tt-ing-card]')];
    if (ttIngCards.length && 'IntersectionObserver' in window) {
      const videoIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const card = entry.target;
            const video = card.querySelector('[data-tt-ing-video]');
            if (!video) return;
            if (entry.isIntersecting) {
              card.classList.add('is-active');
              // Lite mode: first frame only; the card's sound button starts playback
              if (!document.documentElement.classList.contains('mk-lite')) video.play().catch(() => {});
            } else {
              card.classList.remove('is-active');
              if (!card.classList.contains('is-unmuted')) {
                video.pause();
              }
            }
          });
        },
        { threshold: 0.45 }
      );
      ttIngCards.forEach((card) => {
        videoIo.observe(card);
        const soundBtn = card.querySelector('[data-tt-ing-sound]');
        const video = card.querySelector('[data-tt-ing-video]');
        soundBtn?.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const unmuted = card.classList.toggle('is-unmuted');
          ttIngCards.forEach((other) => {
            if (other === card) return;
            other.classList.remove('is-unmuted');
            const ov = other.querySelector('[data-tt-ing-video]');
            if (ov) ov.muted = true;
          });
          if (!video) return;
          video.muted = !unmuted;
          try {
            await video.play();
          } catch (_) {
            /* ignore */
          }
        });
      });
    } else if (ttIngCards.length) {
      ttIngCards.forEach((card) => {
        const video = card.querySelector('[data-tt-ing-video]');
        video?.play().catch(() => {});
      });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-care-story]').forEach(initCareStory);
  });
})();
